import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import FileAttachments from '../components/FileAttachments';
import { BookOpen, Plus, XCircle, CheckCircle2, ChevronDown, ChevronUp, Paperclip, Send, RotateCcw, Pencil, Trash2, AlertCircle } from 'lucide-react';
import { BadgeEstadoFirma, ModalPasarPdteFirma, ModalRevertirBorrador, esEstadoFirmado, IconoBloqueado } from '../components/FlujoFirmaAcciones';
import { ModalEliminarRegistro, esAdmin } from '../components/RegistroAcciones';

// ═══════════════════════════════════════════════════════════════
// MODAL: Crear nueva formación
// ═══════════════════════════════════════════════════════════════
function ModalFormacion({ onClose, onGuardado }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    fecha: today, titulo: '', descripcion: '', instructor: '',
    participantes: '', duracion_horas: '', lugar: '', resultado: '', observaciones: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savedId, setSavedId] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const guardar = async () => {
    if (!form.titulo || !form.fecha) { setError('Título y fecha son obligatorios'); return; }
    setLoading(true);
    try { const res = await api.post('/registros/formacion', form); setSavedId(res.data.id); }
    catch (e) { setError(e.response?.data?.error || 'Error'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <BookOpen size={18} color="var(--color-primary)" /> Nueva Sesión de Formación
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-danger">{error}</div>}
          {savedId && <div className="alert alert-success"><CheckCircle2 size={16} /> Sesión guardada en estado Borrador.</div>}
          <div className="form-group"><label className="form-label">Título de la formación <span className="required">*</span></label>
            <input className="form-control" value={form.titulo} onChange={e => set('titulo', e.target.value)} disabled={!!savedId} /></div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Fecha <span className="required">*</span></label>
              <input type="date" className="form-control" value={form.fecha} onChange={e => set('fecha', e.target.value)} disabled={!!savedId} /></div>
            <div className="form-group"><label className="form-label">Duración (horas)</label>
              <input type="number" step="0.5" className="form-control" value={form.duracion_horas} onChange={e => set('duracion_horas', e.target.value)} disabled={!!savedId} /></div>
          </div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Instructor</label>
              <input className="form-control" value={form.instructor} onChange={e => set('instructor', e.target.value)} disabled={!!savedId} /></div>
            <div className="form-group"><label className="form-label">Lugar</label>
              <input className="form-control" value={form.lugar} onChange={e => set('lugar', e.target.value)} disabled={!!savedId} /></div>
          </div>
          <div className="form-group"><label className="form-label">Participantes</label>
            <textarea className="form-control" rows={3} value={form.participantes} onChange={e => set('participantes', e.target.value)} disabled={!!savedId} /></div>
          <div className="form-group"><label className="form-label">Descripción</label>
            <textarea className="form-control" rows={3} value={form.descripcion} onChange={e => set('descripcion', e.target.value)} disabled={!!savedId} /></div>
          <div className="form-group"><label className="form-label">Resultado / Evaluación</label>
            <input className="form-control" value={form.resultado} onChange={e => set('resultado', e.target.value)} disabled={!!savedId} /></div>
          <div className="form-group"><label className="form-label">Observaciones</label>
            <textarea className="form-control" rows={2} value={form.observaciones} onChange={e => set('observaciones', e.target.value)} disabled={!!savedId} /></div>
          <FileAttachments seccion="formacion" entidadTipo="formacion" entidadId={savedId} label="Adjuntar acta firmada / material" />
        </div>
        <div className="modal-footer">
          {!savedId ? (
            <><button className="btn btn-outline" onClick={onClose}>Cancelar</button>
              <button className="btn btn-primary" onClick={guardar} disabled={loading}>
                {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <CheckCircle2 size={16} />} Guardar
              </button></>
          ) : (
            <><button className="btn btn-outline" onClick={onClose}>Cerrar</button>
              <button className="btn btn-primary" onClick={onGuardado}><CheckCircle2 size={16} /> Finalizar</button></>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODAL: Editar formación (admin + contraseña)
// ═══════════════════════════════════════════════════════════════
function ModalEditarFormacion({ registro, onClose, onActualizado }) {
  const [form, setForm] = useState({
    fecha: registro.fecha, titulo: registro.titulo || '',
    descripcion: registro.descripcion || '', instructor: registro.instructor || '',
    participantes: registro.participantes || '', duracion_horas: registro.duracion_horas || '',
    lugar: registro.lugar || '', resultado: registro.resultado || '',
    observaciones: registro.observaciones || ''
  });
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleGuardar = async () => {
    if (!password) { setError('Debes introducir tu contraseña'); return; }
    setLoading(true); setError('');
    try {
      await api.put(`/registros/formacion/${registro.id}`, { ...form, password });
      onActualizado();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al actualizar');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Pencil size={20} color="#3b82f6" /> Editar Formación
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="alert alert-warning" style={{ fontSize: '0.85rem' }}>
            <AlertCircle size={16} /> <strong>Acción restringida:</strong> Solo administradores. Se requiere contraseña.
          </div>

          <div className="form-group"><label className="form-label">Título</label>
            <input className="form-control" value={form.titulo} onChange={e => set('titulo', e.target.value)} /></div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Fecha</label>
              <input type="date" className="form-control" value={form.fecha} onChange={e => set('fecha', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Duración (horas)</label>
              <input type="number" step="0.5" className="form-control" value={form.duracion_horas} onChange={e => set('duracion_horas', e.target.value)} /></div>
          </div>
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Instructor</label>
              <input className="form-control" value={form.instructor} onChange={e => set('instructor', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Lugar</label>
              <input className="form-control" value={form.lugar} onChange={e => set('lugar', e.target.value)} /></div>
          </div>
          <div className="form-group"><label className="form-label">Participantes</label>
            <textarea className="form-control" rows={2} value={form.participantes} onChange={e => set('participantes', e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Descripción</label>
            <textarea className="form-control" rows={2} value={form.descripcion} onChange={e => set('descripcion', e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Resultado</label>
            <input className="form-control" value={form.resultado} onChange={e => set('resultado', e.target.value)} /></div>
          <div className="form-group"><label className="form-label">Observaciones</label>
            <textarea className="form-control" rows={2} value={form.observaciones} onChange={e => set('observaciones', e.target.value)} /></div>

          <div className="form-group" style={{ marginTop: 16, padding: 12, background: '#fef3c7', borderRadius: 8, borderLeft: '4px solid #f59e0b' }}>
            <label className="form-label" style={{ color: '#92400e', fontWeight: 600 }}>🔐 Confirma con tu contraseña</label>
            <input type="password" className="form-control" value={password} onChange={e => setPassword(e.target.value)} placeholder="Tu contraseña actual" />
          </div>

          {error && <div className="alert alert-danger">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleGuardar} disabled={loading || !password}>
            {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <CheckCircle2 size={16} />}
            Guardar cambios
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// FILA
// ═══════════════════════════════════════════════════════════════
function FilaFormacion({ r, isAdmin, onPasarPdte, onRevertir, onEditar, onEliminar }) {
  const [expanded, setExpanded] = useState(false);
  const estado = r.estado || 'borrador';
  const bloqueado = esEstadoFirmado(estado, false);

  return (
    <>
      <tr>
        <td>{new Date(r.fecha).toLocaleDateString('es-ES')}</td>
        <td style={{ fontWeight: 600, maxWidth: 240 }}>{r.titulo}</td>
        <td style={{ fontSize: '0.82rem' }}>{r.instructor || '—'}</td>
        <td style={{ fontSize: '0.78rem', color: 'var(--color-text-2)', maxWidth: 180 }}>{r.participantes || '—'}</td>
        <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem' }}>{r.duracion_horas ? `${r.duracion_horas}h` : '—'}</td>
        <td style={{ fontSize: '0.82rem' }}>{r.lugar || '—'}</td>
        <td><BadgeEstadoFirma estado={estado} /></td>
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FileAttachments seccion="formacion" entidadTipo="formacion" entidadId={r.id} compact />
            <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            
            {estado === 'borrador' && (
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onPasarPdte(r)} 
                title="Pasar a Pdte. firma" style={{ color: '#f59e0b' }}>
                <Send size={13} />
              </button>
            )}
            {estado === 'pendiente_firma' && isAdmin && (
              <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onRevertir(r)} 
                title="Revertir a Borrador (admin)" style={{ color: '#6b7280' }}>
                <RotateCcw size={13} />
              </button>
            )}
            {isAdmin && !bloqueado && (
              <>
                <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onEditar(r)} 
                  title="Editar (admin)" style={{ color: '#3b82f6' }}>
                  <Pencil size={13} />
                </button>
                <button className="btn btn-ghost btn-icon btn-sm" onClick={() => onEliminar(r)} 
                  title="Eliminar (admin)" style={{ color: 'var(--color-danger)' }}>
                  <Trash2 size={13} />
                </button>
              </>
            )}
            {bloqueado && <IconoBloqueado estado={estado} />}
          </div>
        </td>
      </tr>
      {expanded && (
        <tr><td colSpan={8} style={{ padding: '0 14px 14px', background: 'var(--color-gray-1)' }}>
          <FileAttachments seccion="formacion" entidadTipo="formacion" entidadId={r.id} label="Subir acta de formación firmada o material entregado" />
        </td></tr>
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════
// PÁGINA
// ═══════════════════════════════════════════════════════════════
export default function FormacionPage() {
  const [registros, setRegistros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editarRegistro, setEditarRegistro] = useState(null);
  const [eliminarRegistro, setEliminarRegistro] = useState(null);
  const [pasarPdteRegistro, setPasarPdteRegistro] = useState(null);
  const [revertirRegistro, setRevertirRegistro] = useState(null);
  const [success, setSuccess] = useState('');
  const isAdmin = esAdmin();

  const load = () => { setLoading(true); api.get('/registros/formacion').then(r => setRegistros(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const mostrarSuccess = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <BookOpen size={24} color="var(--color-primary)" /> Registro de Formación
          </h1>
          <p>{registros.length} sesiones · Cada formación se firma individualmente por la Directora Técnica</p>
        </div>
        <button className="btn btn-primary" onClick={() => setModal(true)}><Plus size={16} /> Nueva Formación</button>
      </div>

      {success && <div className="alert alert-success" style={{ marginBottom: 16 }}><CheckCircle2 size={16} /> {success}</div>}

      <div className="card">
        {loading ? <div className="loading-overlay"><div className="spinner" /></div> :
          registros.length === 0 ? (
            <div className="empty-state"><BookOpen size={48} /><h3>Sin formaciones registradas</h3></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Fecha</th><th>Título</th><th>Instructor</th><th>Participantes</th><th>Duración</th><th>Lugar</th><th>Estado</th><th><Paperclip size={13} /></th></tr></thead>
                <tbody>{registros.map(r => 
                  <FilaFormacion key={r.id} r={r} isAdmin={isAdmin}
                    onPasarPdte={setPasarPdteRegistro}
                    onRevertir={setRevertirRegistro}
                    onEditar={setEditarRegistro}
                    onEliminar={setEliminarRegistro} />)}
                </tbody>
              </table>
            </div>
          )}
      </div>

      {modal && <ModalFormacion onClose={() => setModal(false)} onGuardado={() => { setModal(false); load(); }} />}
      
      {pasarPdteRegistro && <ModalPasarPdteFirma 
        endpoint={`/registros/formacion/${pasarPdteRegistro.id}/pasar-pdte-firma`}
        descripcion={`FORM-${pasarPdteRegistro.id} · ${pasarPdteRegistro.titulo}`}
        onClose={() => setPasarPdteRegistro(null)}
        onConfirmado={() => { setPasarPdteRegistro(null); load(); mostrarSuccess('Marcado como pendiente de firma.'); }} />}
      
      {revertirRegistro && <ModalRevertirBorrador 
        endpoint={`/registros/formacion/${revertirRegistro.id}/reset-estado`}
        descripcion={`FORM-${revertirRegistro.id} · ${revertirRegistro.titulo}`}
        onClose={() => setRevertirRegistro(null)}
        onConfirmado={() => { setRevertirRegistro(null); load(); mostrarSuccess('Revertido a borrador'); }} />}
      
      {editarRegistro && <ModalEditarFormacion registro={editarRegistro} 
        onClose={() => setEditarRegistro(null)}
        onActualizado={() => { setEditarRegistro(null); load(); mostrarSuccess('Formación actualizada'); }} />}
      
      {eliminarRegistro && <ModalEliminarRegistro 
        endpoint={`/registros/formacion/${eliminarRegistro.id}`}
        descripcion={`FORM-${eliminarRegistro.id} · ${eliminarRegistro.titulo}`}
        onClose={() => setEliminarRegistro(null)}
        onEliminado={() => { setEliminarRegistro(null); load(); mostrarSuccess('Formación eliminada'); }} />}
    </div>
  );
}
